///
/// EarthBrowser
///
/// Created by Matt Giger on 9/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// One member pin of an expanded multi-location placemark. Created and
/// owned by the parent PlacemarkNode's expansion state machine, rendered
/// as a child node (transform relative to the anchor). Same marker
/// mechanics as the anchor pin; focusing a member frames just that
/// member and opens the member callout (with a breadcrumb back to the
/// whole placemark).
///

import { GroundFocusAdapter } from "@/earthbrowser/datasets/GroundFocusAdapter"
import { type InfoPanelData, type InfoPanelProvider } from "@/earthbrowser/datasets/InfoPanelProvider"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { EmberDomeNode } from "@/earthbrowser/entities/EmberDome/EmberDomeNode"
import { geographic } from "@/earthbrowser/utils/geographic"
import { type PlacemarkLocation } from "@/redux/placemarksSlice"
import { pushTargetToHistory } from "@/utils/navigationHistory"
import { type Camera } from "@stovokor/camera/Camera"
import { type RenderActionSet } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type FocusTransitionConfig } from "@stovokor/gestures/FocusTarget"
import { GestureNode } from "@stovokor/gestures/GestureNode"
import { type GestureHitResult } from "@stovokor/gestures/GestureRecognizer"
import { trig } from "@stovokor/math"
import { matrix } from "@stovokor/math/matrix"
import { dot } from "@stovokor/math/vector"
import {
	cHitPadding,
	cMarkerSphereRadius,
	cPinFarFill,
	cPinFill,
	cPinHeightAboveTerrainKm,
	cPinMaxAngular,
	cPinMinAngular,
	cSinglePinFramingKm,
	paletteColor,
	type PlacemarkNode,
} from "./PlacemarkNode"

export class PlacemarkMemberNode extends GestureNode implements InfoPanelProvider {
	public readonly id: string
	public readonly focusable: boolean = true
	public coordinate: geographic

	private ebContext: EarthBrowserContext
	private parentPin: PlacemarkNode
	private member: PlacemarkLocation
	private markerDome: EmberDomeNode
	private cameraAdapter: GroundFocusAdapter
	private elevationResolved = false
	private terrainVersion = -1
	private hovered = false
	private domeScale = cMarkerSphereRadius

	constructor(context: EarthBrowserContext, parentPin: PlacemarkNode, member: PlacemarkLocation) {
		super(context)
		this.id = `pin-${parentPin.feature.id}:${member.id}`
		this.ebContext = context
		this.parentPin = parentPin
		this.member = member

		this.coordinate = new geographic(member.lon * trig.rad, member.lat * trig.rad, cPinHeightAboveTerrainKm)
		this.applyTerrainElevation()

		this.markerDome = new EmberDomeNode(context)
		this.markerDome.fillAlpha = cPinFill
		this.markerDome.farFillAlpha = cPinFarFill
		this.markerDome.minAngularSize = cPinMinAngular
		this.markerDome.transform = matrix.scale(cMarkerSphereRadius)
		this.updateMarkerColor()
		this.addNode(this.markerDome)

		// Frame just this member; also wires the tap recognizer / EntityHoverHandler
		this.cameraAdapter = new GroundFocusAdapter(
			context, this, this.coordinate.cartesian, cSinglePinFramingKm
		)
	}

	public get memberId(): string {
		return this.member.id
	}

	/** Parent placemark feature (lets deep links resolve member pins as
	 *  regular dataset scene objects) */
	public get feature() {
		return this.parentPin.feature
	}

	/**
	 * Sit the pin on the terrain (or at the member's explicit altitude).
	 * The node renders as a child of the anchor pin, so the transform is
	 * the offset from the anchor's world position.
	 */
	private applyTerrainElevation(): void {
		let elevationKm: number
		this.terrainVersion = this.ebContext.terrainVersion
		if (this.member.altitudeM !== undefined) {
			elevationKm = this.member.altitudeM / 1000
			this.elevationResolved = true
		} else {
			elevationKm = this.ebContext.displayElevationKm(this.coordinate)
			this.elevationResolved = this.ebContext.baseElevation.loaded
		}
		this.coordinate = new geographic(
			this.coordinate.longitude,
			this.coordinate.latitude,
			elevationKm + cPinHeightAboveTerrainKm
		)
		this.transform = matrix.translate(
			this.coordinate.cartesian.subtract(this.parentPin.anchorPosition)
		)
	}

	// ========== InfoPanelProvider ==========

	public getInfoPanelData(): InfoPanelData {
		const placemark = this.parentPin.feature.placemark
		return {
			title: this.member.name ?? placemark.name,
			subtitle: this.member.name ? placemark.name : undefined,
			theme: "placemark",
			contentType: "placemark",
			contentData: {
				type: "placemark",
				placemark,
				memberId: this.member.id,
			},
		}
	}

	// ========== Focus Lifecycle ==========

	public onFocusGained(camera: Camera): void {
		this.cameraAdapter.attach(camera)
		pushTargetToHistory("pin", `${this.parentPin.feature.id}:${this.member.id}`)
	}

	public onFocusLost(): void {
		this.cameraAdapter.detach()
	}

	public onFocusUpdate(): void {
		this.cameraAdapter.update()
	}

	public focusTransition(camera: Camera): FocusTransitionConfig {
		return {
			target: this.cameraAdapter.getTargetCameraState(camera),
			params: {
				duration: this.cameraAdapter.transitionDuration(camera),
				easing: "easeInOut",
			},
		}
	}

	// ========== Command Handlers ==========

	public handleCommand(command: string, data?: unknown): boolean {
		return this.cameraAdapter.handleCommand(command, data)
	}

	private updateMarkerColor(): void {
		const color = this.parentPin.feature.placemark.color
		if (this.hovered) {
			this.markerDome.color = paletteColor(color, 1)
			this.markerDome.fillAlpha = 1
		} else {
			this.markerDome.color = paletteColor(color, 0.9)
			this.markerDome.fillAlpha = cPinFill
		}
	}

	/** Hover cue from EntityHoverHandler: brighten the would-be-picked pin */
	public onHoverHighlight(hovered: boolean): void {
		this.hovered = hovered
		this.updateMarkerColor()
	}

	override render(state: RenderState): RenderActionSet[] {
		if (!this.visible) {
			return []
		}

		// Snap onto the terrain once the async elevation layer has data, and
		// re-seat when the terrain exaggeration changes
		if ((!this.elevationResolved || this.terrainVersion !== this.ebContext.terrainVersion) && this.ebContext.baseElevation.loaded) {
			this.applyTerrainElevation()
		}

		// Apparent-size ceiling so the pin stays a discrete marker up close
		const distance = state.camera.position.subtract(this.coordinate.cartesian).length
		const targetScale = Math.min(cMarkerSphereRadius, distance * cPinMaxAngular)
		if (Math.abs(targetScale - this.domeScale) > this.domeScale * 0.02) {
			this.domeScale = targetScale
			this.markerDome.transform = matrix.scale(targetScale)
		}

		return super.render(state)
	}

	/** Apparent-size picking, same approach as the anchor pin */
	override gestureIntersection(state: RenderState): GestureHitResult | undefined {
		const rayDepth = -dot(state.ray.origin, state.ray.direction)
		if (rayDepth <= 0) {
			return undefined
		}
		const missDistance = state.ray.pointAt(rayDepth).length

		const apparentRadius = Math.max(
			Math.min(cMarkerSphereRadius, rayDepth * cPinMaxAngular),
			rayDepth * cPinMinAngular
		)
		if (missDistance > apparentRadius * cHitPadding) {
			return undefined
		}

		return {
			node: this,
			hitPoint: state.ray.pointAt(rayDepth),
			distance: rayDepth + missDistance,
		}
	}
}
